import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { login as apiLogin, LoginRequest } from '../api/admin';

interface AuthContextType {
  token: string | null;
  isAuthenticated: boolean;
  login: (data: Omit<LoginRequest, 'deviceId'>) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('adminToken'));
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    // Sync state with local storage on mount
    const savedToken = localStorage.getItem('adminToken');
    if (savedToken) {
      setToken(savedToken);
    }
    
    // Ensure deviceId exists
    let deviceId = localStorage.getItem('adminDeviceId');
    if (!deviceId) {
       deviceId = crypto.randomUUID();
       localStorage.setItem('adminDeviceId', deviceId);
    }
  }, []);

  const login = async (data: Omit<LoginRequest, 'deviceId'>) => {
    setLoading(true);
    try {
      const deviceId = localStorage.getItem('adminDeviceId') || crypto.randomUUID();
      if (!localStorage.getItem('adminDeviceId')) {
          localStorage.setItem('adminDeviceId', deviceId);
      }
      
      const response = await apiLogin({ ...data, deviceId });
      if (response && response.accessToken) {
        localStorage.setItem('adminToken', response.accessToken);
        setToken(response.accessToken);
        // RefreshToken is now handled via HttpOnly cookie
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    // localStorage.removeItem('adminRefreshToken');
    setToken(null);
  };

  return (
    <AuthContext.Provider value={{ token, isAuthenticated: !!token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
